import React, { useState, useCallback, useEffect } from 'react';
import { fmtDateTimeMedium, todayIso, toIsoDate } from '../utils/dateUtils';
import useIsMobile from '../hooks/useIsMobile';

const fmtDt = (v) => fmtDateTimeMedium(v);
const thirtyDaysAgo = () => {
  const d = new Date(); d.setDate(d.getDate() - 30);
  return toIsoDate(d);
};

const STARS = [1, 2, 3, 4, 5];
const starLabel = { 5: 'Excellent', 4: 'Very Good', 3: 'Good', 2: 'Fair', 1: 'Poor' };
const sourceLabel = { HOUSEKEEPING: 'Housekeeping', SERVICE_REQUEST: 'Service Request', FOOD_ORDER: 'Food Order' };
const sourceBg = { HOUSEKEEPING: '#dbeafe', SERVICE_REQUEST: '#ede9fe', FOOD_ORDER: '#fef3c7' };
const sourceColor = { HOUSEKEEPING: '#1e40af', SERVICE_REQUEST: '#6b21a8', FOOD_ORDER: '#92400e' };

const sentimentBg = { POSITIVE: '#f0fdf4', NEGATIVE: '#fef2f2', NEUTRAL: 'var(--theme-bg-light)' };
const sentimentColor = { POSITIVE: 'var(--theme-success)', NEGATIVE: 'var(--theme-danger)', NEUTRAL: 'var(--theme-text-secondary)' };
const sentimentIcon = { POSITIVE: '+', NEGATIVE: '-', NEUTRAL: '~' };
const urgencyColor = { HIGH: 'var(--theme-danger)', MEDIUM: '#ca8a04', LOW: 'var(--theme-text-secondary)' };
const categoryLabel = {
  cleanliness: 'Cleanliness', staff_behavior: 'Staff Behavior', food_quality: 'Food Quality',
  amenities: 'Amenities', noise: 'Noise', ac_temperature: 'AC/Temperature', wifi: 'WiFi',
  plumbing: 'Plumbing', billing: 'Billing', room_condition: 'Room Condition',
  check_in_out: 'Check-in/out', parking: 'Parking', general: 'General',
};
const deptLabel = {
  housekeeping: 'Housekeeping', front_desk: 'Front Desk', fnb: 'F&B',
  maintenance: 'Maintenance', management: 'Management', general: 'General',
};

const StarDisplay = ({ rating, size = 16 }) => (
  <span style={{ fontSize: size, letterSpacing: 1 }}>
    {STARS.map(s => (
      <span key={s} style={{ color: s <= rating ? '#f59e0b' : '#d1d5db' }}>&#9733;</span>
    ))}
  </span>
);

function PmsGuestFeedback({ token, selectedProperty, aiEnabled }) {
  const { isMobile } = useIsMobile();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [avgRating, setAvgRating] = useState(0);
  const [breakdown, setBreakdown] = useState({ star5: 0, star4: 0, star3: 0, star2: 0, star1: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fromDate, setFromDate] = useState(thirtyDaysAgo());
  const [toDate, setToDate] = useState(todayIso());
  const [filterSource, setFilterSource] = useState('');
  const [filterMinRating, setFilterMinRating] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // AI Sentiment state
  const [activeTab, setActiveTab] = useState('feedback');
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeResult, setAnalyzeResult] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [feedbackAnalyses, setFeedbackAnalyses] = useState({});

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const loadData = useCallback(async (pg = 0) => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      let url = `/api/v1/pms/guest-feedback?from=${fromDate}&to=${toDate}&limit=${pageSize}&offset=${pg * pageSize}`;
      if (filterSource) url += `&source_type=${filterSource}`;
      if (filterMinRating > 0) url += `&min_rating=${filterMinRating}`;
      const res = await fetch(url, { headers: headers() });
      const data = await res.json();
      if (res.ok) {
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setAvgRating(data.avg_rating || 0);
        setBreakdown(data.breakdown || {});
        setPage(pg);
        // Load AI analyses for these feedback rows
        if (aiEnabled && data.rows?.length) {
          loadFeedbackAnalyses(data.rows.map(r => r.feedback_id));
        }
      } else {
        setError(data.error || 'Failed to load');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    }
    setLoading(false);
  }, [selectedProperty, headers, fromDate, toDate, filterSource, filterMinRating, aiEnabled]);

  const loadFeedbackAnalyses = async (feedbackIds) => {
    if (!feedbackIds.length) return;
    try {
      const res = await fetch(`/api/v1/ai/sentiment/feedback-analysis?feedback_ids=${feedbackIds.join(',')}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setFeedbackAnalyses(prev => ({ ...prev, ...data.analyses }));
      }
    } catch (_) { /* non-critical */ }
  };

  const loadInsights = useCallback(async () => {
    if (!selectedProperty?.id || !aiEnabled) return;
    setInsightsLoading(true);
    try {
      const res = await fetch('/api/v1/ai/sentiment/insights?days=30', { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setInsights(data);
      }
    } catch (_) { /* non-critical */ }
    setInsightsLoading(false);
  }, [selectedProperty, headers, aiEnabled]);

  useEffect(() => { loadData(0); }, [selectedProperty?.id]);
  useEffect(() => { if (aiEnabled) loadInsights(); }, [loadInsights, aiEnabled]);

  const runSentimentAnalysis = async () => {
    setAnalyzing(true); setError(''); setAnalyzeResult(null);
    try {
      const res = await fetch('/api/v1/ai/sentiment/analyze', { method: 'POST', headers: headers(), body: '{}' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Analysis failed');
      setAnalyzeResult(data);
      // Reload insights & per-row analyses
      loadInsights();
      if (rows.length) loadFeedbackAnalyses(rows.map(r => r.feedback_id));
    } catch (err) {
      setError(err.message);
    } finally { setAnalyzing(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const ratedCount = breakdown.star5 + breakdown.star4 + breakdown.star3 + breakdown.star2 + breakdown.star1;

  const kpiCard = (label, value, color, sub) => (
    <div style={{ flex: 1, minWidth: 130, background: '#fff', borderRadius: 8, border: '1px solid var(--theme-border)', padding: '0.8rem 1rem', textAlign: 'center' }}>
      <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)', fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: color || '#1e293b' }}>{value}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ padding: '1rem' }}>
      <h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Guest Feedback</h2>
      <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>
        Ratings and comments from guests on housekeeping, service requests, and food orders.
      </p>

      {error && <div style={{ background: '#fef2f2', color: 'var(--theme-danger)', padding: '0.5rem 0.8rem', borderRadius: 6, marginBottom: '0.8rem', fontSize: '0.85rem', fontWeight: 600, border: '1px solid #fecaca' }}>{error} <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--theme-danger)', cursor: 'pointer', fontWeight: 700 }}>x</button></div>}

      {/* Tabs — show AI Insights tab only when enabled */}
      {aiEnabled && (
        <div style={{ display: 'flex', gap: 0, marginBottom: '1rem', borderBottom: '2px solid var(--theme-border)' }}>
          {[{ key: 'feedback', label: 'Feedback' }, { key: 'ai-insights', label: 'AI Insights' }].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ padding: '0.6rem 1.2rem', border: 'none', borderBottom: activeTab === tab.key ? '2px solid #7c3aed' : '2px solid transparent',
                background: 'none', fontWeight: activeTab === tab.key ? 700 : 500, color: activeTab === tab.key ? '#7c3aed' : 'var(--theme-text-secondary)',
                cursor: 'pointer', fontSize: '0.9rem', marginBottom: -2 }}>
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* ═══════ TAB 1: Feedback List ═══════ */}
      {(activeTab === 'feedback' || !aiEnabled) && (
        <>
          {/* KPI Cards */}
          <div style={{ display: 'flex', gap: '0.7rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            {kpiCard('Avg Rating', avgRating > 0 ? avgRating.toFixed(1) : '-', avgRating >= 4 ? 'var(--theme-success)' : avgRating >= 3 ? '#ca8a04' : avgRating > 0 ? 'var(--theme-danger)' : 'var(--theme-text-secondary)',
              avgRating > 0 ? starLabel[Math.round(avgRating)] || '' : ''
            )}
            {kpiCard('Total Reviews', ratedCount, '#1e40af')}
            {kpiCard('5 Star', breakdown.star5, 'var(--theme-success)', ratedCount > 0 ? `${((breakdown.star5 / ratedCount) * 100).toFixed(0)}%` : '')}
            {kpiCard('4 Star', breakdown.star4, '#65a30d', ratedCount > 0 ? `${((breakdown.star4 / ratedCount) * 100).toFixed(0)}%` : '')}
            {kpiCard('3 Star', breakdown.star3, '#ca8a04')}
            {kpiCard('1-2 Star', breakdown.star1 + breakdown.star2, 'var(--theme-danger)')}
          </div>

          {/* Rating Breakdown Bar */}
          {ratedCount > 0 && (
            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid var(--theme-border)', padding: '0.8rem 1rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.5rem', color: '#334155' }}>Rating Distribution</div>
              {[5, 4, 3, 2, 1].map(star => {
                const count = breakdown[`star${star}`] || 0;
                const pct = ratedCount > 0 ? (count / ratedCount) * 100 : 0;
                return (
                  <div key={star} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 3 }}>
                    <span style={{ width: 50, fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>{star} &#9733;</span>
                    <div style={{ flex: 1, height: 14, background: 'var(--theme-border-light)', borderRadius: 7, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: star >= 4 ? 'var(--theme-success)' : star === 3 ? '#ca8a04' : 'var(--theme-danger)', borderRadius: 7, transition: 'width 0.3s' }} />
                    </div>
                    <span style={{ width: 50, fontSize: '0.75rem', color: 'var(--theme-text-secondary)', textAlign: 'right' }}>{count} ({pct.toFixed(0)}%)</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Filters */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', background: 'var(--theme-bg-light)', padding: '0.7rem 1rem', borderRadius: 8, border: '1px solid var(--theme-border)' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>From:</label>
            <input type="date" value={fromDate} onChange={e => { const v = e.target.value; setFromDate(v); if (toDate && v && toDate < v) setToDate(v); }} style={{ padding: '0.3rem 0.5rem' }} />
            <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>To:</label>
            <input type="date" value={toDate} min={fromDate || undefined} onChange={e => { const v = e.target.value; setToDate(v); if (fromDate && v && fromDate > v) setFromDate(v); }} style={{ padding: '0.3rem 0.5rem' }} />
            <select value={filterSource} onChange={e => setFilterSource(e.target.value)} style={{ padding: '0.3rem 0.5rem' }}>
              <option value="">All Types</option>
              <option value="HOUSEKEEPING">Housekeeping</option>
              <option value="SERVICE_REQUEST">Service Request</option>
              <option value="FOOD_ORDER">Food Order</option>
            </select>
            <select value={filterMinRating} onChange={e => setFilterMinRating(Number(e.target.value))} style={{ padding: '0.3rem 0.5rem' }}>
              <option value="0">All Ratings</option>
              <option value="4">4+ Stars</option>
              <option value="3">3+ Stars</option>
              <option value="1">1+ Stars</option>
            </select>
            <button onClick={() => loadData(0)} style={{ padding: '0.35rem 0.8rem', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}>
              Search
            </button>
            {aiEnabled && (
              <button onClick={runSentimentAnalysis} disabled={analyzing}
                style={{ padding: '0.35rem 0.8rem', background: analyzing ? 'var(--theme-text-muted)' : '#7c3aed', color: '#fff', border: 'none', borderRadius: 5, cursor: analyzing ? 'default' : 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
                {analyzing ? 'Analyzing...' : 'Analyze with AI'}
              </button>
            )}
            <span style={{ fontSize: '0.82rem', color: 'var(--theme-text-secondary)', marginLeft: '0.5rem' }}>{total} feedback(s)</span>
          </div>

          {analyzeResult && (
            <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 6, padding: '0.5rem 0.8rem', marginBottom: '0.8rem', fontSize: '0.82rem', color: '#7c3aed', fontWeight: 600 }}>
              Analyzed {analyzeResult.analyzed_count} new feedback(s) in {analyzeResult.response_time_ms}ms ({((Number(analyzeResult.usage?.input_tokens) || 0) + (Number(analyzeResult.usage?.output_tokens) || 0)).toLocaleString('en-IN')} tokens)
            </div>
          )}

          {loading && <div style={{ color: '#2563eb', marginBottom: '0.5rem' }}>Loading...</div>}

          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
              <thead>
                <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Room</th>
                  <th style={thStyle}>Guest</th>
                  <th style={thStyle}>Type</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Rating</th>
                  <th style={thStyle}>Comment</th>
                  {aiEnabled && <th style={{ ...thStyle, textAlign: 'center' }}>Sentiment</th>}
                  {aiEnabled && <th style={thStyle}>Category</th>}
                  {aiEnabled && <th style={{ ...thStyle, textAlign: 'center' }}>Urgency</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const ai = feedbackAnalyses[r.feedback_id];
                  return (
                    <tr key={r.feedback_id} style={{ borderBottom: '1px solid var(--theme-border)', background: r.rating <= 2 ? '#fef2f2' : ai?.urgency === 'HIGH' ? '#fef3c7' : undefined }}>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDt(r.created_at)}</td>
                      <td style={{ ...tdStyle, fontWeight: 600, fontSize: '0.85rem' }}>{r.room_number || '-'}</td>
                      <td style={{ ...tdStyle, fontSize: '0.84rem' }}>{r.guest_name || '-'}</td>
                      <td style={tdStyle}>
                        <span style={{ display: 'inline-block', padding: '0.1rem 0.45rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700, background: sourceBg[r.source_type] || 'var(--theme-border-light)', color: sourceColor[r.source_type] || '#475569' }}>
                          {sourceLabel[r.source_type] || r.source_type}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <StarDisplay rating={r.rating} size={14} />
                      </td>
                      <td style={{ ...tdStyle, color: '#475569', maxWidth: aiEnabled ? 220 : 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={r.comment || ''}>
                        {r.comment || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>No comment</span>}
                      </td>
                      {aiEnabled && (
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {ai ? (
                            <span style={{ display: 'inline-block', padding: '0.1rem 0.45rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700,
                              background: sentimentBg[ai.sentiment] || 'var(--theme-bg-light)', color: sentimentColor[ai.sentiment] || 'var(--theme-text-secondary)' }}>
                              {sentimentIcon[ai.sentiment]} {ai.sentiment}
                            </span>
                          ) : r.comment ? (
                            <span style={{ fontSize: '0.72rem', color: '#cbd5e1' }}>—</span>
                          ) : null}
                        </td>
                      )}
                      {aiEnabled && (
                        <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#475569' }}
                          title={ai?.issue_summary || ''}>
                          {ai ? (categoryLabel[ai.category] || ai.category) : ''}
                        </td>
                      )}
                      {aiEnabled && (
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {ai?.urgency && (
                            <span style={{ display: 'inline-block', padding: '0.1rem 0.4rem', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700,
                              color: urgencyColor[ai.urgency] || 'var(--theme-text-secondary)', background: `${urgencyColor[ai.urgency] || 'var(--theme-text-secondary)'}15` }}>
                              {ai.urgency}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {!rows.length && !loading && (
                  <tr><td colSpan={aiEnabled ? 9 : 6} style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--theme-text-secondary)' }}>No feedback found for the selected criteria</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > pageSize && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.8rem', justifyContent: 'center' }}>
              <button disabled={page === 0} onClick={() => loadData(page - 1)}
                style={{ padding: '0.3rem 0.8rem', border: '1px solid #cbd5e0', borderRadius: 5, cursor: page === 0 ? 'default' : 'pointer', background: '#fff' }}>
                Previous
              </button>
              <span style={{ fontSize: '0.85rem', color: '#334155' }}>Page {page + 1} of {totalPages}</span>
              <button disabled={page >= totalPages - 1} onClick={() => loadData(page + 1)}
                style={{ padding: '0.3rem 0.8rem', border: '1px solid #cbd5e0', borderRadius: 5, cursor: page >= totalPages - 1 ? 'default' : 'pointer', background: '#fff' }}>
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* ═══════ TAB 2: AI Insights Dashboard ═══════ */}
      {activeTab === 'ai-insights' && aiEnabled && (
        <div>
          {insightsLoading && <div style={{ color: '#7c3aed', marginBottom: '0.5rem' }}>Loading insights...</div>}

          {insights && (
            <>
              {/* Coverage + Analyze */}
              <div style={{ display: 'flex', gap: '0.7rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 200, background: '#f5f3ff', borderRadius: 8, border: '1px solid #ddd6fe', padding: '0.7rem 1rem' }}>
                  <span style={{ fontSize: '0.82rem', color: '#7c3aed', fontWeight: 600 }}>
                    {insights.coverage?.analyzed || 0} / {insights.coverage?.total_with_comments || 0} feedback analyzed
                  </span>
                  {insights.coverage?.total_with_comments > insights.coverage?.analyzed && (
                    <span style={{ fontSize: '0.78rem', color: 'var(--theme-text-muted)', marginLeft: '0.5rem' }}>
                      ({insights.coverage.total_with_comments - insights.coverage.analyzed} pending)
                    </span>
                  )}
                </div>
                <button onClick={runSentimentAnalysis} disabled={analyzing}
                  style={{ padding: '0.45rem 1rem', background: analyzing ? 'var(--theme-text-muted)' : '#7c3aed', color: '#fff', border: 'none', borderRadius: 5, cursor: analyzing ? 'default' : 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                  {analyzing ? 'Analyzing...' : 'Analyze New Feedback'}
                </button>
              </div>

              {analyzeResult && (
                <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 6, padding: '0.5rem 0.8rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#7c3aed', fontWeight: 600 }}>
                  Analyzed {analyzeResult.analyzed_count} feedback(s) — {((Number(analyzeResult.usage?.input_tokens) || 0) + (Number(analyzeResult.usage?.output_tokens) || 0)).toLocaleString('en-IN')} tokens
                </div>
              )}

              {/* Sentiment Distribution KPIs */}
              <div style={{ display: 'flex', gap: '0.7rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                {(insights.sentiment || []).map(s => (
                  <div key={s.sentiment} style={{ flex: 1, minWidth: 120, background: '#fff', borderRadius: 8, border: '1px solid var(--theme-border)', padding: '0.7rem 1rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-secondary)', fontWeight: 600, marginBottom: 3 }}>{s.sentiment}</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 700, color: sentimentColor[s.sentiment] || '#1e293b' }}>{s.count}</div>
                  </div>
                ))}
              </div>

              {/* Two-column layout: Categories + Departments */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                {/* Top Issue Categories */}
                <div style={{ background: '#fff', borderRadius: 8, border: '1px solid var(--theme-border)', padding: '0.8rem 1rem' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '0.6rem' }}>Top Issue Categories</div>
                  {(insights.categories || []).slice(0, 8).map(c => {
                    const total = Number(c.count);
                    const neg = Number(c.negative_count);
                    return (
                      <div key={c.category} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 4 }}>
                        <span style={{ width: 100, fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>{categoryLabel[c.category] || c.category}</span>
                        <div style={{ flex: 1, height: 12, background: 'var(--theme-border-light)', borderRadius: 6, overflow: 'hidden', display: 'flex' }}>
                          <div style={{ width: `${total > 0 ? (neg / total) * 100 : 0}%`, height: '100%', background: 'var(--theme-danger)', borderRadius: '6px 0 0 6px' }} />
                          <div style={{ width: `${total > 0 ? ((total - neg) / total) * 100 : 0}%`, height: '100%', background: 'var(--theme-success)' }} />
                        </div>
                        <span style={{ width: 45, fontSize: '0.72rem', color: 'var(--theme-text-secondary)', textAlign: 'right' }}>{total}</span>
                      </div>
                    );
                  })}
                  {!insights.categories?.length && <div style={{ fontSize: '0.82rem', color: 'var(--theme-text-muted)' }}>No data yet</div>}
                </div>

                {/* Department Performance */}
                <div style={{ background: '#fff', borderRadius: 8, border: '1px solid var(--theme-border)', padding: '0.8rem 1rem' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '0.6rem' }}>Department Performance</div>
                  {(insights.departments || []).map(d => {
                    const total = Number(d.count);
                    const pos = Number(d.positive_count);
                    const neg = Number(d.negative_count);
                    const score = total > 0 ? Math.round((pos / total) * 100) : 0;
                    return (
                      <div key={d.department} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 5 }}>
                        <span style={{ width: 90, fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>{deptLabel[d.department] || d.department}</span>
                        <div style={{ flex: 1, height: 12, background: 'var(--theme-border-light)', borderRadius: 6, overflow: 'hidden' }}>
                          <div style={{ width: `${score}%`, height: '100%', background: score >= 70 ? 'var(--theme-success)' : score >= 40 ? '#ca8a04' : 'var(--theme-danger)', borderRadius: 6, transition: 'width 0.3s' }} />
                        </div>
                        <span style={{ width: 60, fontSize: '0.72rem', color: 'var(--theme-text-secondary)', textAlign: 'right' }}>{score}% pos</span>
                        <span style={{ width: 30, fontSize: '0.72rem', color: 'var(--theme-danger)', textAlign: 'right' }}>{neg}</span>
                      </div>
                    );
                  })}
                  {!insights.departments?.length && <div style={{ fontSize: '0.82rem', color: 'var(--theme-text-muted)' }}>No data yet</div>}
                </div>
              </div>

              {/* Problem Rooms Alert */}
              {insights.problem_rooms?.length > 0 && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.8rem 1rem', marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--theme-danger)', marginBottom: '0.5rem' }}>
                    Rooms with Repeated Negative Feedback
                  </div>
                  {insights.problem_rooms.map(r => (
                    <div key={r.room_number} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 3, fontSize: '0.82rem' }}>
                      <span style={{ fontWeight: 700, color: '#1e293b', minWidth: 60 }}>Room {r.room_number}</span>
                      <span style={{ color: 'var(--theme-danger)', fontWeight: 600 }}>{r.negative_count} complaints</span>
                      <span style={{ color: 'var(--theme-text-secondary)' }}>—</span>
                      <span style={{ color: '#475569' }}>{(r.categories || []).map(c => categoryLabel[c] || c).join(', ')}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* High Urgency Alerts */}
              {insights.alerts?.length > 0 && (
                <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '0.8rem 1rem', marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#92400e', marginBottom: '0.5rem' }}>
                    High Urgency Alerts
                  </div>
                  {insights.alerts.map(a => (
                    <div key={a.id} style={{ borderBottom: '1px solid #fde68a', padding: '0.4rem 0', fontSize: '0.82rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, color: '#1e293b' }}>Room {a.room_number || '?'}</span>
                        <span style={{ fontSize: '0.72rem', color: '#92400e', background: '#fef3c7', padding: '0.1rem 0.3rem', borderRadius: 3, fontWeight: 700 }}>
                          {categoryLabel[a.category] || a.category}
                        </span>
                        <StarDisplay rating={a.rating} size={11} />
                        <span style={{ color: 'var(--theme-text-secondary)', fontSize: '0.75rem' }}>{a.guest_name || ''}</span>
                      </div>
                      <div style={{ color: '#475569', marginTop: 2 }}>{a.issue_summary}</div>
                      {a.comment && <div style={{ color: 'var(--theme-text-muted)', fontStyle: 'italic', marginTop: 1, fontSize: '0.78rem' }}>"{a.comment}"</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* Sentiment Trend (daily) */}
              {insights.trend?.length > 0 && (
                <div style={{ background: '#fff', borderRadius: 8, border: '1px solid var(--theme-border)', padding: '0.8rem 1rem' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '0.6rem' }}>Sentiment Trend (Last 30 Days)</div>
                  <div style={{ overflowX: 'auto' }}>
                    <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', minHeight: 80, minWidth: insights.trend.length * 28 }}>
                      {insights.trend.map(d => {
                        const max = Math.max(1, d.total);
                        const pctPos = (d.positive / max) * 60;
                        const pctNeg = (d.negative / max) * 60;
                        const pctNeu = (d.neutral / max) * 60;
                        return (
                          <div key={d.date} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 24 }}
                            title={`${d.date}: +${d.positive} -${d.negative} ~${d.neutral}`}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <div style={{ width: 18, height: pctPos, background: 'var(--theme-success)', borderRadius: '3px 3px 0 0' }} />
                              <div style={{ width: 18, height: pctNeu, background: 'var(--theme-text-muted)' }} />
                              <div style={{ width: 18, height: pctNeg, background: 'var(--theme-danger)', borderRadius: '0 0 3px 3px' }} />
                            </div>
                            <span style={{ fontSize: '0.6rem', color: 'var(--theme-text-muted)', marginTop: 2, transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>
                              {d.date.slice(5)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>
                      <span><span style={{ display: 'inline-block', width: 8, height: 8, background: 'var(--theme-success)', borderRadius: 2, marginRight: 3 }} />Positive</span>
                      <span><span style={{ display: 'inline-block', width: 8, height: 8, background: 'var(--theme-text-muted)', borderRadius: 2, marginRight: 3 }} />Neutral</span>
                      <span><span style={{ display: 'inline-block', width: 8, height: 8, background: 'var(--theme-danger)', borderRadius: 2, marginRight: 3 }} />Negative</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {!insights && !insightsLoading && (
            <div style={{ textAlign: 'center', color: 'var(--theme-text-secondary)', padding: '2rem' }}>
              Click "Analyze New Feedback" to start AI sentiment analysis.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const thStyle = { padding: '0.5rem', textAlign: 'left', fontSize: '0.82rem', fontWeight: 600 };
const tdStyle = { padding: '0.45rem 0.5rem', fontSize: '0.82rem' };

export default PmsGuestFeedback;
